import { faFloppyDisk } from '@fortawesome/pro-regular-svg-icons';
import { Group, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdTagCreative, DisplayCreative } from '@feathr/blackbox';
import { CreativeClass } from '@feathr/blackbox';
import {
  Button,
  ButtonValid,
  FullScreenModal,
  Icon,
  Pill,
  useVideoFrameExtract,
} from '@feathr/components';
import { flattenErrors } from '@feathr/hooks';

import { getCreativeThumbnailUrl } from '../Creatives.utils';
import CreativeForm from './CreativeForm';
import PreviewPanel from './PreviewPanel';
import type { IThumbnailCarouselItem } from './ThumbnailCarousel';
import ThumbnailCarousel, { DEFAULT_VISIBLE_COUNT } from './ThumbnailCarousel';

import * as styles from './CreativeModal.css';

export interface ICreativeModalProps {
  creative?: DisplayCreative;
  /** Array of thumbnail items to display in the carousel. */
  thumbnailItems?: IThumbnailCarouselItem[];
  opened: boolean;
  /** Callback when closing without saving (X button or Cancel). Discards dirty changes automatically. */
  onClose: () => void;
  /** Callback when "Save and close" is clicked. If not provided, just closes. */
  onSave?: () => void | Promise<void>;
  /** Callback when a thumbnail is selected in the carousel. */
  onSelectCreative?: (id: string) => void;
  /**
   * Fields to skip validation for.
   * Useful when a field will be set during the save process (e.g., ad_video_id).
   */
  skipValidationFields?: string[];
  /** Callback to open Bannersnack editor for builder-created creatives */
  onEditInBannersnack?: () => void;
  /** Whether the modal is currently saving */
  isSaving?: boolean;
}

function CreativeModal({
  creative,
  thumbnailItems,
  opened,
  onClose,
  onSave,
  onSelectCreative,
  skipValidationFields = [],
  onEditInBannersnack,
  isSaving = false,
}: Readonly<ICreativeModalProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const isAdTag = creative?.get('_cls') === CreativeClass.DisplayAdTag;
  const adTagHtml = isAdTag ? (creative as AdTagCreative).get('adtag') : undefined;
  const creativeUrl = !isAdTag && creative ? getCreativeThumbnailUrl(creative) : undefined;
  const isVideo = creative?.creativeType === 'Video';
  const {
    thumbnailUrl: videoFrameUrl,
    isLoading: isVideoThumbnailLoading,
    hasError: hasVideoThumbnailError,
  } = useVideoFrameExtract(creativeUrl ?? '', isVideo && !!creativeUrl);

  const previewImageUrl = isVideo ? videoFrameUrl || undefined : creativeUrl;

  const title = (
    <Group gap={theme.spacing['2']}>
      <span>{t('Preview creative')}</span>
      <Pill truncate={false}>{creative?.creativeType ?? t('Image')}</Pill>
    </Group>
  );

  const leftActions =
    thumbnailItems && onSelectCreative ? (
      <div className={styles.carouselWrapper}>
        <ThumbnailCarousel
          items={thumbnailItems}
          onSelect={onSelectCreative}
          selectedId={creative?.id ?? ''}
          visibleCount={Math.min(thumbnailItems.length, DEFAULT_VISIBLE_COUNT)}
        />
      </div>
    ) : undefined;

  // Get validation errors for the save button (empty array validates all constrained fields)
  // Filter out any fields that should be skipped (e.g., fields set during save)
  const allErrors = creative ? creative.validate([], false, 'grouped').errors : {};
  const filteredErrors = Object.fromEntries(
    Object.entries(allErrors).filter(([field]) => !skipValidationFields.includes(field)),
  );
  const validationErrors = flattenErrors(filteredErrors);

  /** Discard changes and close */
  const handleCancel = useCallback((): void => {
    creative?.discardDirty();
    onClose();
  }, [creative, onClose]);

  /** Run onSave if provided, then persist dirty changes and close */
  const handleSave = useCallback(async (): Promise<void> => {
    await onSave?.();
    await creative?.patchDirty();
    onClose();
  }, [creative, onClose, onSave]);

  const rightActions = (
    <>
      {onSave && (
        <Button name={'creative-modal-cancel'} onClick={handleCancel}>
          {t('Cancel')}
        </Button>
      )}
      <ButtonValid
        errors={validationErrors}
        isLoading={isSaving}
        name={'creative-modal-save'}
        onClick={handleSave}
        prefix={<Icon icon={faFloppyDisk} />}
        type={'primary'}
      >
        {t('Save and close')}
      </ButtonValid>
    </>
  );

  return (
    <FullScreenModal
      className={styles.modal}
      closeOnEscape={false}
      leftActions={leftActions}
      onClose={handleCancel}
      opened={opened}
      rightActions={rightActions}
      theme={'flush'}
      title={title}
    >
      <div className={styles.body}>
        <div className={styles.previewArea}>
          <PreviewPanel
            adTagHtml={adTagHtml}
            contentHeight={creative?.get('height')}
            contentWidth={creative?.get('width')}
            creativeName={creative?.name}
            hasVideoThumbnailError={isVideo && hasVideoThumbnailError}
            imageUrl={isVideo ? undefined : previewImageUrl}
            isAdTag={isAdTag}
            isMetaCreative={creative?.isFacebook}
            isVideoThumbnailLoading={isVideo && isVideoThumbnailLoading && !hasVideoThumbnailError}
            videoUrl={isVideo ? creativeUrl : undefined}
          />
        </div>
        <div className={styles.sidebar}>
          {creative && (
            <CreativeForm creative={creative} onEditInBannersnack={onEditInBannersnack} />
          )}
        </div>
      </div>
    </FullScreenModal>
  );
}

export default observer(CreativeModal);
